// Skeleton.jsx - Reusable skeleton loading components

// ─── Base ─────────────────────────────────────────────────────────────────────

const Skeleton = ({ className = '', style, ...props }) => (
  <div
    className={`animate-pulse rounded bg-[var(--color-surface-elevated)] ${className}`}
    style={style}
    {...props}
  />
);

// ─── Primitives ───────────────────────────────────────────────────────────────

Skeleton.Text = ({ width = 'w-full', height = 'h-4', className = '' }) => (
  <Skeleton className={`${width} ${height} ${className}`} />
);

Skeleton.Circle = ({ size = 'w-10 h-10', className = '' }) => (
  <Skeleton className={`${size} rounded-full ${className}`} />
);

Skeleton.TrendTileGrid = ({ count = 4, columnsClassName = 'sm:grid-cols-2 xl:grid-cols-4' }) => (
  <div className={`grid gap-4 ${columnsClassName}`}>
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={`trend-tile-skeleton-${index}`}
        className="animate-pulse rounded-xl border border-[var(--color-tile-border)] bg-[var(--color-surface-row)] px-4 py-3.5 shadow-[var(--shadow-tile-subtle)]"
      >
        <div className="h-4 w-20 rounded-full bg-[var(--color-surface-input)]/80" />
        <div className="mt-4 h-8 w-28 rounded-full bg-[var(--color-surface-input)]/85" />
        <div className="mt-2 h-4 w-16 rounded-full bg-[var(--color-surface-input)]/70" />
      </div>
    ))}
  </div>
);

Skeleton.TrendChartCard = ({
  titleWidth = 'w-56',
  chartHeight = 'h-[320px]',
  className = '',
}) => (
  <div className={`animate-pulse rounded-2xl border border-[var(--color-tile-border)] bg-[var(--color-surface-row)] px-6 py-5 shadow-[var(--shadow-surface)] ${className}`}>
    <div className={`h-7 ${titleWidth} rounded-full bg-[var(--color-surface-input)]/80`} />
    <div className={`mt-6 rounded-2xl bg-[var(--color-surface-input)]/70 ${chartHeight}`} />
  </div>
);

// Generic card wrapper (mobile / legacy use)
Skeleton.Card = ({ className = '', children }) => (
  <div className={`glassmorphism rounded-2xl p-6 ${className}`}>
    {children}
  </div>
);

// ─── KPI Stats Card ───────────────────────────────────────────────────────────
// Matches: bg-slate-900/20 border border-slate-700/50 rounded-xl p-5
// (inside "Spend snapshot" section grid)

Skeleton.StatsCard = ({ delay = 0 }) => (
  <div
    className="animate-pulse rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)] p-5"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-start justify-between mb-3">
      <Skeleton className="w-20 h-3 rounded" />
      <div className="h-8 w-8 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-input)]" />
    </div>
    <Skeleton className="w-28 h-7 rounded mb-2" />
    <Skeleton className="w-24 h-3.5 rounded" />
  </div>
);

// ─── Budget Group Button ───────────────────────────────────────────────────────
// Matches: min-h-[92px] rounded-xl border border-slate-700/50 bg-slate-900/20 p-3

const BudgetGroupCard = ({ delay = 0 }) => (
  <div
    className="flex min-h-[92px] animate-pulse flex-col justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)] p-3"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-center gap-2">
      <div className="h-6 w-6 shrink-0 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-input)]" />
      <Skeleton className="w-14 h-3 rounded" />
    </div>
    <Skeleton className="w-20 h-3.5 rounded mt-4" />
  </div>
);

// ─── Transaction Table Row ─────────────────────────────────────────────────────
// Matches: DataTableRow density="dense"
// Cols: Date (96px) | Merchant (flex) | Amount (140px, right) | Category (220px) | Notes (240px)

Skeleton.TransactionTableRow = ({ delay = 0 }) => (
  <tr
    className="animate-pulse border-b border-[var(--color-border)]"
    style={{ animationDelay: `${delay}s` }}
  >
    {/* Date */}
    <td className="py-3 px-4 align-middle">
      <Skeleton className="w-16 h-3.5 rounded" />
    </td>
    {/* Merchant — logo + name + account subtitle */}
    <td className="py-3 px-4 align-middle">
      <div className="flex items-center gap-2.5 min-w-0">
        <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
        <div className="min-w-0">
          <Skeleton className="w-36 h-3.5 rounded mb-1.5" />
          <Skeleton className="w-20 h-3 rounded" />
        </div>
      </div>
    </td>
    {/* Amount */}
    <td className="py-3 px-4 text-right align-middle">
      <Skeleton className="w-16 h-3.5 rounded ml-auto" />
    </td>
    {/* Category chip */}
    <td className="py-3 px-4 align-middle">
      <Skeleton className="w-24 h-6 rounded-full" />
    </td>
    {/* Notes */}
    <td className="py-3 px-4 align-middle">
      <Skeleton className="w-28 h-3.5 rounded" />
    </td>
  </tr>
);

// ─── Transactions Table ────────────────────────────────────────────────────────
// Matches: DataSurface variant + DataTable density="dense"
// Outer: rounded-2xl border border-slate-700/60 bg-slate-900/20

Skeleton.TransactionsTable = ({ count = 6 }) => (
  <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]">
    <table className="w-full" style={{ minWidth: '860px' }}>
      <colgroup>
        <col style={{ width: '96px' }} />
        <col />
        <col style={{ width: '140px' }} />
        <col style={{ width: '220px' }} />
        <col style={{ width: '240px' }} />
      </colgroup>
      <thead>
        <tr className="border-b border-[var(--color-border)]">
          <th className="py-3 px-4 text-left"><Skeleton className="w-10 h-3.5 rounded" /></th>
          <th className="py-3 px-4 text-left"><Skeleton className="w-20 h-3.5 rounded" /></th>
          <th className="py-3 px-4 text-right"><Skeleton className="w-14 h-3.5 rounded ml-auto" /></th>
          <th className="py-3 px-4 text-left"><Skeleton className="w-16 h-3.5 rounded" /></th>
          <th className="py-3 px-4 text-left"><Skeleton className="w-10 h-3.5 rounded" /></th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton.TransactionTableRow key={i} delay={i * 0.05} />
        ))}
      </tbody>
    </table>
  </div>
);

Skeleton.ReviewBar = () => (
  <div data-testid="review-bar-skeleton" className="flex h-[100px] w-full animate-pulse items-center justify-between gap-4 rounded-2xl border border-purple-500/20 bg-[var(--color-shell-bg)] px-5 sm:h-[96px] sm:px-7">
    <div className="flex items-center gap-4 min-w-0 flex-1">
      <div className="h-12 w-12 shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-hover)] sm:h-11 sm:w-11" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-5 w-56 max-w-[80%]" />
        <Skeleton className="h-3.5 w-32" />
      </div>
    </div>
    <div className="hidden h-10 w-36 shrink-0 rounded-xl bg-indigo-500/35 sm:block" />
  </div>
);

Skeleton.HouseholdMembers = ({ rowCount = 4 }) => (
  <div className="space-y-8 animate-pulse">
    <div>
      <Skeleton className="w-24 h-3 rounded mb-3" />
      <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)]">
        <div className="grid grid-cols-[30%_32%_18%_10%_10%] gap-0 border-b border-[var(--color-border)] px-5 py-3">
          <Skeleton className="w-16 h-3.5 rounded" />
          <Skeleton className="w-14 h-3.5 rounded" />
          <Skeleton className="w-10 h-3.5 rounded" />
          <Skeleton className="w-12 h-3.5 rounded" />
          <Skeleton className="w-6 h-3.5 rounded ml-auto" />
        </div>
        {Array.from({ length: rowCount }).map((_, i) => (
          <div key={i} className="grid grid-cols-[30%_32%_18%_10%_10%] gap-0 border-b border-[var(--color-border)] px-5 py-4 last:border-b-0">
            <Skeleton className="w-28 h-4 rounded" />
            <Skeleton className="w-40 h-4 rounded" />
            <Skeleton className="w-14 h-6 rounded-full" />
            <Skeleton className="w-12 h-6 rounded-full" />
            <Skeleton className="w-8 h-8 rounded-lg ml-auto" />
          </div>
        ))}
      </div>
    </div>

    <div>
      <Skeleton className="w-28 h-3 rounded mb-3" />
      <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)]">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="grid grid-cols-[32%_26%_22%_20%] gap-0 border-b border-[var(--color-border)] px-5 py-4 last:border-b-0">
            <Skeleton className="w-40 h-4 rounded" />
            <Skeleton className="w-24 h-4 rounded" />
            <Skeleton className="w-24 h-4 rounded" />
            <Skeleton className="w-20 h-8 rounded-lg ml-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

Skeleton.CategoryInspectorRules = () => (
  <div className="space-y-3 animate-pulse" data-testid="category-rules-skeleton">
    <Skeleton className="w-40 h-3.5 rounded" />
    <div className="flex flex-wrap gap-1.5">
      <Skeleton className="w-20 h-6 rounded-md" />
      <Skeleton className="w-16 h-6 rounded-md" />
      <Skeleton className="w-24 h-6 rounded-md" />
      <Skeleton className="w-14 h-6 rounded-md" />
    </div>
    <Skeleton className="w-32 h-3 rounded" />
  </div>
);

Skeleton.AppBootstrap = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[var(--color-sidebar-bg)] via-[var(--color-bg-secondary)] to-[var(--color-bg)] px-4">
    <div className="w-full max-w-xl animate-pulse rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-8 shadow-2xl">
      <div className="flex items-center gap-3 mb-8">
        <Skeleton className="w-7 h-7 rounded-md" />
        <Skeleton className="w-40 h-6 rounded" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

// ─── Budget Category Row (legacy / sidebar) ────────────────────────────────────

Skeleton.CategoryRow = ({ hasChildren = false }) => (
  <div className="flex items-center justify-between rounded-xl p-3 hover:bg-[var(--color-surface-hover)]">
    <div className="flex items-center gap-3">
      {hasChildren && <Skeleton className="w-4 h-4 rounded" />}
      <Skeleton className="w-24 h-4 rounded" />
    </div>
    <Skeleton className="w-16 h-5 rounded" />
  </div>
);

// Budget sidebar skeleton (legacy)
Skeleton.BudgetSidebar = () => (
  <Skeleton.Card className="card-glow">
    <div className="flex items-center gap-2 mb-6">
      <Skeleton className="w-5 h-5 rounded" />
      <Skeleton className="w-40 h-6 rounded" />
    </div>
    <div className="space-y-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton.CategoryRow key={i} hasChildren />
      ))}
    </div>
  </Skeleton.Card>
);

// ─── Dashboard ────────────────────────────────────────────────────────────────
// Matches the four main sections rendered on /dashboard:
//  1. Spend Snapshot   — gradient container + 4-col KPI grid
//  2. Budget Health    — gradient container + left spend summary + right group grid
//  3. Actions Panel    — gradient container + action row placeholders
//  4. Recent Transactions — gradient container + dense DataTable

Skeleton.Dashboard = () => (
  <div className="space-y-6">

    {/* ── 1. Spend Snapshot ────────────────────────────────────── */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-from)] to-[var(--color-panel-to)] px-8 pb-7 pt-8 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3 mb-8">
        <Skeleton className="w-6 h-6 rounded-md" />
        <Skeleton className="w-36 h-6 rounded" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Skeleton.StatsCard delay={0} />
        <Skeleton.StatsCard delay={0.07} />
        <Skeleton.StatsCard delay={0.14} />
        <Skeleton.StatsCard delay={0.21} />
      </div>
    </div>

    {/* ── 2. Budget Health ─────────────────────────────────────── */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-from)] to-[var(--color-panel-to)] px-8 pb-7 pt-8 shadow-[var(--shadow-card)]">
      {/* Header with title + button placeholder */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div className="flex items-center gap-3">
          <Skeleton className="w-6 h-6 rounded-md" />
          <Skeleton className="w-32 h-6 rounded" />
        </div>
        <Skeleton className="w-24 h-8 rounded-lg" />
      </div>
      {/* 12-col grid: left spend summary (col-4) + right group grid (col-8) */}
      <div className="grid grid-cols-12 gap-5 items-stretch">
        {/* Left: total spend summary */}
        <div className="col-span-12 md:col-span-4">
          <div className="flex h-full min-h-[184px] flex-col justify-start space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)] p-5 animate-pulse">
            <Skeleton className="w-28 h-3 rounded" />
            <Skeleton className="w-32 h-11 rounded" />
            <Skeleton className="w-36 h-3.5 rounded" />
          </div>
        </div>
        {/* Right: by-group cards */}
        <div className="col-span-12 md:col-span-8">
          <div className="flex h-full min-h-[184px] flex-col justify-start rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)] p-5">
            <Skeleton className="w-16 h-3 rounded mb-4" />
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <BudgetGroupCard delay={0} />
              <BudgetGroupCard delay={0.06} />
              <BudgetGroupCard delay={0.12} />
              <BudgetGroupCard delay={0.18} />
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* ── 3. Actions Panel ─────────────────────────────────────── */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-from)] to-[var(--color-panel-to)] p-6 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="w-6 h-6 rounded-md" />
        <Skeleton className="w-20 h-5 rounded" />
      </div>
      <div className="space-y-3">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)] px-5 py-4 animate-pulse"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <div className="h-9 w-9 shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-input)]" />
            <div className="flex-1 min-w-0">
              <Skeleton className="w-48 h-3.5 rounded mb-2" />
              <Skeleton className="w-28 h-3 rounded" />
            </div>
            <Skeleton className="w-16 h-8 rounded-lg shrink-0" />
          </div>
        ))}
      </div>
    </div>

    {/* ── 4. Recent Transactions ───────────────────────────────── */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-from)] to-[var(--color-panel-to)] p-6 shadow-[var(--shadow-card)]">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Skeleton className="w-6 h-6 rounded-md" />
          <Skeleton className="w-44 h-6 rounded" />
        </div>
        <Skeleton className="w-36 h-8 rounded-lg" />
      </div>
      <Skeleton.TransactionsTable count={8} />
    </div>

  </div>
);

// ─── Mobile Dashboard ─────────────────────────────────────────────────────────

Skeleton.MobileDashboard = () => (
  <div className="px-4 pt-4 space-y-4">
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="w-24 h-6 rounded" />
        <Skeleton className="w-8 h-8 rounded-lg" />
      </div>
      <div className="flex items-center justify-between">
        <Skeleton className="w-10 h-5 rounded" />
        <Skeleton className="w-32 h-5 rounded" />
        <Skeleton className="w-10 h-5 rounded" />
      </div>
    </div>
    <Skeleton.Card className="card-glow">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <Skeleton className="w-40 h-5 rounded" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)] p-4">
            <div className="flex items-center justify-between">
              <Skeleton className="w-40 h-4 rounded" />
              <Skeleton className="w-6 h-4 rounded" />
            </div>
            <Skeleton className="w-24 h-3 rounded mt-3" />
          </div>
        ))}
      </div>
    </Skeleton.Card>
  </div>
);

export default Skeleton;
